import {MithrilComponent, Signal} from '@bitstillery/mithril'
import nouislider, {PipsMode} from 'nouislider'
import type {API as SliderApi} from 'nouislider'
import {unique_id} from '@bitstillery/common/lib/utils'
import {watch, type Unwatch} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

export interface SliderAttrs {
    minimum: number
    maximum: number
    /** A bound that is null is inactive and shows as the scale's end. */
    model: Signal<(number | null)[]>
    infinity?: boolean
    step?: number
}

export class Slider extends MithrilComponent<SliderAttrs> {
    id = `slider-${unique_id(8, false)}`
    slider: SliderApi | undefined
    watchers: Unwatch[] = []

    override oncreate(vnode: m.Vnode<SliderAttrs, this>): void {
        const element = document.getElementById(this.id)
        if (!element) {
            return
        }

        const arr = vnode.attrs.model.value
        if (arr.length < 2) {
            return
        }

        // Inactive state; default to minimum and maximum.
        if (arr[1] === null) {
            arr[1] = vnode.attrs.infinity ? Infinity : vnode.attrs.maximum
        }

        // nouislider only accepts numbers in range; convert Infinity to maximum
        const toSlider = (val: (number | null)[] | undefined): number[] => {
            const [low, high] = val ?? []
            return val?.length === 2
                ? [
                      typeof low === 'number' && Number.isFinite(low) ? low : vnode.attrs.minimum,
                      typeof high === 'number' && Number.isFinite(high) ? high : vnode.attrs.maximum,
                  ]
                : [vnode.attrs.minimum, vnode.attrs.maximum]
        }

        const formatTooltip = (val: number): string =>
            Number.isFinite(val) ? (vnode.attrs.infinity && val === vnode.attrs.maximum ? '∞' : val.toFixed(0)) : ''

        const slider = nouislider.create(element, {
            connect: true,
            format: {
                from: (value: string) => Number(value),
                to: (val: number) => (vnode.attrs.infinity && val === vnode.attrs.maximum ? Infinity : val.toFixed(0)),
            },
            pips: {density: 5, format: {to: formatTooltip}, mode: PipsMode.Range},
            range: {
                '33%': Math.floor(vnode.attrs.maximum / 3),
                '66%': Math.floor(vnode.attrs.maximum / 3) * 2,
                max: vnode.attrs.maximum,
                min: vnode.attrs.minimum,
            },
            start: toSlider(vnode.attrs.model.value),
            step: vnode.attrs.step || 1,
            tooltips: [{to: formatTooltip}, {to: formatTooltip}],
        })
        this.slider = slider

        // Two-way binding: state → slider (use toSlider so Infinity becomes maximum)
        this.watchers.push(
            watch(vnode.attrs.model, () => {
                slider.set(toSlider(vnode.attrs.model.value))
            }),
        )

        // UI-element → state: convert maximum back to Infinity for infinity filters
        slider.on('change', (value: (number | string)[]) => {
            const parsed = value.map((i) => Number(i))
            if (vnode.attrs.infinity && parsed[1] === vnode.attrs.maximum) {
                parsed[1] = Infinity
            }
            vnode.attrs.model.value.splice(0, vnode.attrs.model.value.length, ...parsed)
        })
    }

    override onremove() {
        // Cleanup two-way binding. Guard: oncreate may have returned early without creating the slider.
        if (this.slider) {
            this.slider.destroy()
        }
        this.watchers.map((watcher) => watcher())
    }

    view() {
        return (
            <div className={'c-slider'}>
                <div id={this.id} />
            </div>
        )
    }
}
